import { useEffect, useState } from 'react';
import { useUser } from './useUser';
import { parseEntityVariant, type EntityVariant } from '../types/common';

/**
 * Retourne le variant de l'entité courante.
 *
 * Source de vérité par ordre de priorité :
 *   1. `user.company.variant` (réponse `/users/me` — vérité backend stable)
 *   2. `sessionStorage.signup_variant` (choix Register avant que la company
 *      ne soit créée — pertinent pendant le flow d'onboarding)
 *   3. Default 'standard'
 *
 * Cette priorité garantit qu'une coopérative existante reste affichée comme
 * telle (sa company.variant prime), tandis qu'un nouveau signup en cours
 * voit déjà le vocabulaire coopératif avant que sa company existe en DB.
 *
 * Réactivité : écoute les `storage` events pour réagir aux changements de
 * `signup_variant` faits par d'autres composants de l'app (ex : le selector
 * inline du EnterpriseIdentificationForm pour les comptes neufs sans
 * passage préalable par /register). Sans ça, le toggle ne déclencherait
 * pas de re-render des libellés affichés ailleurs.
 */
function readSignupVariant(): EntityVariant {
  if (typeof window === 'undefined') return 'standard';
  try {
    const stored = parseEntityVariant(sessionStorage.getItem('signup_variant'));
    if (stored) return stored;
  } catch {
    // sessionStorage indisponible (SSR, privacy mode) → ignore
  }
  return 'standard';
}

export function useCompanyVariant(): EntityVariant {
  const { user } = useUser();
  const [sessionVariant, setSessionVariant] = useState<EntityVariant>(readSignupVariant);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Le storage event natif n'est émis que pour les changements faits dans
    // d'autres onglets. Le selector inline dispatch un StorageEvent manuel
    // sur la même fenêtre pour synchroniser les hooks de l'onglet courant.
    const handler = (e: StorageEvent) => {
      if (e.key !== 'signup_variant' && e.key !== null) return;
      setSessionVariant(readSignupVariant());
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // 1) Vérité backend — prime toujours
  const backendVariant = parseEntityVariant(user?.company?.variant);
  if (backendVariant) return backendVariant;

  // 2) Fallback sessionStorage observé (flow d'onboarding)
  return sessionVariant;
}
